/**
 * COMMON-PASSWORD DENYLIST — 613 entries, bundled, no network.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS DUPLICATED, DELIBERATELY, AND THE DUPLICATION IS TESTED.
 *
 *   backend/src/game/auth/common-passwords.ts   ← the CONTROL
 *   frontend/src/engine/auth/common-passwords.ts ← the UX mirror
 *
 * The two must stay byte-identical. `COMMON_PASSWORD_LIST_FINGERPRINT` below is
 * asserted by BOTH suites (`backend/test/game-register.spec.ts` and
 * `frontend/test/auth-register.test.ts`), so editing one copy fails a test in
 * that package and the only way back to green is to copy the file across.
 *
 * There is no shared package to put it in: `shared/sim` and `shared/proto` are
 * the deterministic rules engine and the wire protocol, and a password denylist
 * is neither. Adding a third workspace package for one array would cost more
 * than a fingerprint test.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHAT IS IN IT: the passwords that appear at the top of every public breach
 * corpus — keyboard walks, repeated digits, the sports/first-name/brand words,
 * the "clever" substitutions that are not clever, and the operator defaults
 * (`admin`, `changeme`, `passw0rd`). It is not, and cannot be, HaveIBeenPwned.
 * It is the cheap 90%: it costs nothing, needs no network, leaks nothing about
 * the password being checked, and stops the guesses an attacker tries first.
 *
 * WHAT IS NOT IN IT: anything under 4 characters (the length rule already
 * refuses those), and anything with a capital letter — every entry is lowercase
 * because `isCommonPassword` lowercases its input before looking here. Adding an
 * upper-case entry would make it unreachable, so the tests assert the invariant.
 */

/** Every entry: lowercase, at least 4 characters, sorted, unique. */
export const COMMON_PASSWORDS: readonly string[] = [
  "000000", "0987654321", "1029384756", "111111", "11111111", "1111111111", "112233", "1212",
  "121212", "123123", "123123123", "123321", "1234", "12345", "1234554321", "123456",
  "1234567", "12345678", "123456789", "1234567890", "123456a", "123456aa", "12345a",
  "1234qwer", "123abc", "123qwe", "131313", "141414", "147258369", "151515", "159753",
  "161616", "171717", "181818", "191919", "1a2b3c4d", "1q2w3e", "1q2w3e4r", "1q2w3e4r5t",
  "1qaz", "1qaz2wsx", "1qaz2wsx3edc", "1qazxsw2", "2000", "202020", "2112", "212121",
  "22222222", "232323", "242424", "252525", "262626", "272727", "27653", "282828", "292929",
  "2wsx", "303030", "313131", "33333333", "3edc", "44444444", "456456", "4rfv", "5201314",
  "55555", "555555", "5tgb", "654321", "666666", "66666666", "696969", "6yhn", "741852963",
  "777777", "7777777", "888888", "88888888", "963852741", "987654321", "999999", "99999999",
  "a123456", "a1b2c3", "a1b2c3d4", "aa123456", "aaaaaa", "aaaaaaaa", "ab123456", "abc123",
  "abc12345", "abc123456", "abcabc", "abcd123", "abcd1234", "abcdef", "admin", "admin123",
  "administrator", "amanda", "amazon", "andrea", "andrew", "android", "angel", "angel1",
  "angels", "animal", "anonymous", "anthony", "apple", "april", "arsenal", "asdasd",
  "asdasd123", "asdf", "asdf1234", "asdfg", "asdfgh", "asdfghjkl", "ashley", "audi", "august",
  "austin", "austin1", "autumn", "avengers", "badboy", "banana", "bank", "barcelona",
  "baseball", "baseball1", "batman", "batman1", "bayern", "bear", "berlin", "bigdick",
  "birthday", "biteme", "blank", "blood", "blowjob", "blowme", "blue", "bond007", "booboo",
  "booger", "boomer", "boss", "boxing", "brandon", "brandy", "brian", "brittany", "bugatti",
  "bulldog", "buster", "butter", "butthead", "camaro", "cameron", "canada", "captain",
  "carlos", "cash", "changeme", "changeme123", "charles", "charlie", "chelsea", "chelsea1",
  "chester", "chicago", "chicago1", "chicken", "chris", "christmas", "christopher", "coffee",
  "compaq", "computer", "cookie", "corvette", "corvette1", "counterstrike", "cowboy",
  "cowboys", "credit", "dakota", "daniel", "david", "death", "debbie", "december", "default",
  "demon", "dennis", "desktop", "devil", "diablo", "diamond", "director", "discord", "doctor",
  "doctor1", "dollar", "dragon", "dragon123", "driver", "ducati", "eagles", "edcrfv", "edward",
  "elizabeth", "emily", "empty", "enter", "everybody", "facebook", "farmer", "february",
  "fender", "ferrari", "ferrari1", "fighter", "firebird", "fireman", "fish", "fisher",
  "fishing", "flower", "flyers", "football", "football1", "fortnite", "freedom", "freedom1",
  "friday", "fucker", "gandalf", "gateway", "gators", "george", "ghost", "ginger", "girls",
  "github", "gmail", "gold", "golf", "golf1", "google", "guest", "guinness", "halloween",
  "hannah", "hardcore", "harley", "harley1", "heather", "heaven", "hello", "hello123",
  "helpdesk", "hidden", "hockey", "hockey1", "holiday", "honda", "hooters", "horny",
  "hostmaster", "hotmail", "house", "hunter", "hunter1", "iceman", "ilovegod", "iloveu",
  "iloveyou", "iloveyou1", "instagram", "internet", "iphone", "ironman", "jack", "jackson",
  "james", "january", "jason", "jasper", "jennifer", "jessica", "jesus", "jesus1",
  "jesuschrist", "john", "johnny", "jonathan", "jordan", "jordan23", "jordan24", "joseph",
  "joshua", "joshua1", "july", "june", "junior", "justin", "juventus", "karate", "katherine",
  "kawasaki", "kevin", "killer", "killer1", "kimberly", "knight", "lakers", "lamborghini",
  "laptop", "laura", "lauren", "league", "leagueoflegends", "leather", "letmein", "letmein1",
  "letmein123", "linkedin", "linux", "liverpool", "lkjhgfdsa", "login", "london", "love",
  "lovely", "loveme", "macbook", "maddog", "madrid", "maggie", "manager", "manchester",
  "march", "marine", "martin", "master", "master1", "matrix", "matthew", "maverick", "megan",
  "melissa", "member", "mercedes", "merlin", "michael", "michael1", "michelle", "mickey",
  "microsoft", "mike", "milan", "miller", "minecraft", "mnbvcxz", "monday", "money", "money1",
  "monkey", "monkey123", "morgan", "moscow", "murder", "mustang", "mustang1", "mynoob",
  "mypassword", "mysql", "nascar", "ncc1701", "netflix", "newpassword", "newyork", "nicholas",
  "nicole", "nicole1", "ninja", "nintendo", "nissan", "nobody", "nokia", "none", "nopassword",
  "nothing", "nothing1", "november", "null", "nurse", "october", "opensesame", "operator",
  "oracle", "orange", "outlook", "p@ssw0rd", "p@ssword", "panther", "paris", "pass", "pass123",
  "passpass", "passw0rd", "password", "password!", "password1", "password1!", "password12",
  "password123", "password2", "password3", "passwords", "peanut", "phoenix", "player",
  "playstation", "please", "poiuytrewq", "pokemon", "police", "porn", "porno", "porsche",
  "postgres", "postmaster", "president", "princess", "princess1", "private", "public",
  "purple", "pussy", "q1w2e3r4", "qazwsx", "qazwsxedc", "qqqqqq", "qwaszx", "qwe123", "qweasd",
  "qweasdzxc", "qweqwe", "qwert", "qwerty", "qwerty1", "qwerty12", "qwerty123", "qwerty1234",
  "qwertyui", "qwertyuiop", "rabbit", "rachel", "ranger", "rangers", "rangers1", "realmadrid",
  "rebecca", "reddit", "redis", "rfvtgb", "rich", "richard", "robert", "roblox", "rocket",
  "root", "root123", "sailor", "samantha", "samantha1", "samsung", "samurai", "sandra",
  "saturday", "scooter", "secret", "secret1", "secret123", "september", "service", "service1",
  "sexsex", "sexy", "shadow", "shadow1", "silver", "silver1", "smokey", "snapchat", "snoopy",
  "soccer", "soccer1", "soldier", "somebody", "spanky", "sparky", "spider", "spiderman",
  "spring", "starwars", "starwars1", "steam", "steelers", "stephanie", "steve", "steven",
  "summer", "summer1", "sunday", "sunday1", "sunshine", "sunshine1", "superman", "superman1",
  "supervisor", "support", "suzuki", "sydney", "sysadmin", "system", "taylor", "teacher",
  "teens", "temp", "temp123", "temporary", "tennis", "tennis1", "test", "test123", "tgbyhn",
  "thomas", "thunder", "thursday", "thx1138", "tiffany", "tiger", "tigers", "tigger", "tiktok",
  "tokyo", "tomcat", "toor", "toronto", "toyota", "triumph", "trustno1", "tuesday", "turtle",
  "twitter", "tyler", "ubuntu", "ujmik", "undefined", "united", "unknown", "user", "vacation",
  "vampire", "viking", "viper", "volvo", "warrior", "webmaster", "wednesday", "weekend",
  "welcome", "welcome1", "welcome123", "werty", "whatever", "willie", "wilson", "windows",
  "winner", "winter", "winter1", "wizard", "woaini1314", "woaini520", "wsxedc", "xavier",
  "xbox", "xsw2zaq1", "xxxxxx", "yahoo", "yamaha", "yamaha1", "yandex", "yankees", "yhnujm",
  "youtube", "zachary", "zaq12wsx", "zaq1xsw2", "zaq1zaq1", "zxcasdqwe", "zxcvb", "zxcvbn",
  "zxcvbnm", "zzzzzz",];

/**
 * Set form — membership is checked on every registration and every keystroke of
 * the strength meter, so it must not be a linear scan.
 */
export const COMMON_PASSWORD_SET: ReadonlySet<string> = new Set(COMMON_PASSWORDS);

/**
 * A cheap, stable fingerprint of the list: its length and a FNV-1a 32-bit hash
 * of the entries joined with `\n`, as 8 lowercase hex characters.
 *
 * Pinned by a test in BOTH packages. A one-word edit to either copy changes it
 * and fails there; syncing the twin is the fix. FNV-1a rather than SHA-256
 * because this has to run identically in Node and in the browser bundle with no
 * imports and no async.
 */
export function fingerprintPasswordList(words: readonly string[]): string {
  let hash = 0x811c9dc5;
  const joined = words.join('\n');
  for (let i = 0; i < joined.length; i += 1) {
    hash ^= joined.charCodeAt(i);
    // FNV prime 16777619, in 32-bit arithmetic that survives JS number precision.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${words.length}:${hash.toString(16).padStart(8, '0')}`;
}

/** Pinned value. See the note at the top of this file before changing it. */
export const COMMON_PASSWORD_LIST_FINGERPRINT = fingerprintPasswordList(COMMON_PASSWORDS);
